import { SESSION_OUTCOMES, type Funnel, type FunnelStepKind, type UseCase } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowDown, ArrowUp, Info, Loader2, Plus, Trash2 } from "lucide-react";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { OUTCOME_LABEL } from "@/components/domain/analytics";
import { PageHeader } from "@/components/domain/misc";
import { Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Input, Label, Select, Textarea } from "@/components/ui/input";
import { AgentPills } from "@/features/analytics-shared/agent-pills";
import { EMPTY_FUNNEL, FUNNEL_LIMITS, type FunnelDraft, type StepDraft, effectiveName, fromSuggestion, hasErrors, moveStep, newStep, toFunnelBody, validateFunnel } from "@/features/funnels/builder-model";
import { STEP_KIND_META, STEP_KINDS, StepKindBadge, WINDOW_LABEL } from "@/features/funnels/meta";
import { q, useCreateFunnel } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/funnels/new")({
  /** `suggestion`: start from a funnel suggested from conversations. */
  validateSearch: z.object({ suggestion: z.string().min(1).optional().catch(undefined) }),
  component: FunnelBuilderPage,
});

const WINDOWS: Funnel["window"][] = ["session", "24h", "7d"];

function FieldError({ id, error, hint }: { id: string; error?: string; hint?: ReactNode }) {
  if (error)
    return (
      <p id={`${id}-error`} role="alert" className="mt-1 text-[11.5px] text-critical-text">
        {error}
      </p>
    );
  return hint ? (
    <p id={`${id}-hint`} className="mt-1 text-[11px] text-fg-3">
      {hint}
    </p>
  ) : null;
}

function FunnelBuilderPage() {
  const navigate = useNavigate();
  const uid = useId();
  const [f, setF] = useState<FunnelDraft>(EMPTY_FUNNEL);
  const { suggestion } = Route.useSearch();
  const suggestions = useQuery({ ...q.funnelSuggestions(), enabled: !!suggestion });
  const prefilled = useRef(false);
  useEffect(() => {
    const s = suggestion ? suggestions.data?.find((x) => x.id === suggestion) : undefined;
    if (s && !prefilled.current) {
      prefilled.current = true;
      setF(fromSuggestion(s));
    }
  }, [suggestion, suggestions.data]);
  const [attempted, setAttempted] = useState(false);
  const useCases = useQuery(q.useCases());
  const settings = useQuery(q.settings());
  const create = useCreateFunnel();
  const ucById = useMemo(() => new Map(useCases.data?.map((u) => [u.id, u])), [useCases.data]);
  const ucName = (id: string) => ucById.get(id)?.name;

  const errors = useMemo(() => validateFunnel(f), [f]);
  const shown = attempted ? errors : { step: {} as typeof errors.step };
  const metadataOnly = settings.data?.privacyMode === "features";
  const hasSemantic = f.steps.some((s) => s.kind === "semantic");

  const setStep = (key: string, patch: Partial<StepDraft>) => setF((d) => ({ ...d, steps: d.steps.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));
  const addStep = (kind: FunnelStepKind) => setF((d) => (d.steps.length >= FUNNEL_LIMITS.steps.max ? d : { ...d, steps: [...d.steps, newStep(kind)] }));

  const submit = () => {
    setAttempted(true);
    if (hasErrors(errors)) {
      requestAnimationFrame(() => document.querySelector<HTMLElement>("[aria-invalid='true']")?.focus());
      return;
    }
    create.mutate(toFunnelBody(f, ucName), { onSuccess: (fn) => void navigate({ to: "/funnels/$funnelId", params: { funnelId: fn.id }, replace: true }) });
  };

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <nav aria-label="Breadcrumb">
            <Link to="/funnels" className="hover:text-fg">
              Funnels
            </Link>
            <span className="px-1.5">/</span>
            <span className="text-fg-2">New funnel</span>
          </nav>
        }
        title="New funnel"
        description="Steps are matched on every session: events, tool calls, outcomes and use cases deterministically, semantic steps as questions in the decision-model pass. Creating starts a backfill over the last 30 days."
        actions={
          <Button variant="ghost" size="sm" asChild>
            <Link to="/funnels">Cancel</Link>
          </Button>
        }
      />

      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]"
      >
        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardHeader title="Basics" />
            <CardBody className="flex flex-col gap-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor={`${uid}-name`}>Name</Label>
                  <Input
                    id={`${uid}-name`}
                    value={f.name}
                    onChange={(e) => setF((d) => ({ ...d, name: e.target.value }))}
                    placeholder="e.g. Return → exchange save"
                    maxLength={FUNNEL_LIMITS.name.max}
                    aria-invalid={shown.name ? true : undefined}
                    aria-describedby={shown.name ? `${uid}-name-error` : undefined}
                  />
                  <FieldError id={`${uid}-name`} error={shown.name} />
                </div>
                <div>
                  <Label htmlFor={`${uid}-desc`}>Description (optional)</Label>
                  <Input id={`${uid}-desc`} value={f.description} onChange={(e) => setF((d) => ({ ...d, description: e.target.value }))} placeholder="What this funnel measures" maxLength={FUNNEL_LIMITS.description} />
                </div>
              </div>
              <fieldset>
                <legend className="mb-1.5 text-xs font-medium text-fg-2">Agents</legend>
                <AgentPills value={f.agentIds} onChange={(ids) => setF((d) => ({ ...d, agentIds: ids ?? [] }))} invalid={!!shown.agentIds} describedBy={shown.agentIds ? `${uid}-agents-error` : undefined} />
                <FieldError id={`${uid}-agents`} error={shown.agentIds} hint="Sessions from these agents enter the funnel." />
              </fieldset>
              <fieldset>
                <legend className="mb-1.5 text-xs font-medium text-fg-2">Window</legend>
                <div role="radiogroup" aria-label="Window" className="flex flex-wrap gap-1.5">
                  {WINDOWS.map((w) => (
                    <button
                      key={w}
                      type="button"
                      role="radio"
                      aria-checked={f.window === w}
                      onClick={() => setF((d) => ({ ...d, window: w }))}
                      className={cn("h-8 cursor-pointer rounded-md border px-3 text-xs font-medium transition-colors", f.window === w ? "border-accent/50 bg-accent-soft text-fg" : "border-border-strong text-fg-2 hover:bg-surface-2")}
                    >
                      {WINDOW_LABEL[w]}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-fg-3">Cross-session windows join sessions of the same end user, e.g. the order placed on the website a day after the chat.</p>
              </fieldset>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Steps" description={`${f.steps.length} of ${FUNNEL_LIMITS.steps.max} · in order; a session must reach each step to count for the next`} />
            <ol className="divide-y">
              {f.steps.map((s, i) => (
                <StepEditor
                  key={s.key}
                  step={s}
                  index={i}
                  count={f.steps.length}
                  errors={shown.step[s.key]}
                  useCases={useCases.data}
                  agentIds={f.agentIds}
                  ucName={ucName}
                  onChange={(patch) => setStep(s.key, patch)}
                  onMove={(to) => setF((d) => ({ ...d, steps: moveStep(d.steps, i, to) }))}
                  onRemove={() => setF((d) => ({ ...d, steps: d.steps.filter((x) => x.key !== s.key) }))}
                />
              ))}
            </ol>
            <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
              <span className="text-xs text-fg-3">Add a step</span>
              {STEP_KINDS.map((k) => (
                <Button key={k} type="button" size="sm" variant="outline" onClick={() => addStep(k)} disabled={f.steps.length >= FUNNEL_LIMITS.steps.max}>
                  <Plus /> {STEP_KIND_META[k].label}
                </Button>
              ))}
            </div>
            {shown.steps ? (
              <p role="alert" className="border-t px-4 py-2 text-[11.5px] text-critical-text">
                {shown.steps}
              </p>
            ) : null}
          </Card>
        </div>

        <div className="flex flex-col gap-3 lg:sticky lg:top-4">
          <Card>
            <CardHeader title="Preview" description={WINDOW_LABEL[f.window]} />
            <CardBody>
              <ol className="flex flex-col gap-1.5">
                {f.steps.map((s, i) => {
                  const name = effectiveName(s, ucName);
                  return (
                    <li key={s.key} className="flex items-center gap-2 text-xs">
                      <span className="grid size-5 shrink-0 place-items-center rounded-full border text-[10px] tabular text-fg-3">{i + 1}</span>
                      <span className={cn("min-w-0 flex-1 truncate", name ? "text-fg" : "italic text-fg-3")}>{name || "Unnamed step"}</span>
                      <StepKindBadge kind={s.kind} withLabel={false} />
                    </li>
                  );
                })}
              </ol>
            </CardBody>
          </Card>
          {hasSemantic && metadataOnly ? (
            <Note tone="warning" icon={<AlertTriangle />}>
              Your privacy mode is <span className="font-medium">features only</span>: no content reaches the analytics model, so semantic steps never match. Use event, tool-call or outcome steps, or change{" "}
              <Link to="/settings" className="underline">
                privacy settings
              </Link>
              .
            </Note>
          ) : null}
          <Note icon={<Info />}>
            Creating starts a backfill over stored, redacted transcripts from the last 30 days (sampled and capped by your enrichment budget). The funnel shows partial numbers while it runs.
          </Note>
          <div className="flex items-center justify-end gap-2">
            {attempted && hasErrors(errors) ? (
              <span role="alert" className="text-xs text-critical-text">
                Fix the highlighted fields.
              </span>
            ) : null}
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <Loader2 className="animate-spin" /> : null} Create funnel
            </Button>
          </div>
        </div>
      </form>
    </Page>
  );
}

function Note({ children, icon, tone }: { children: ReactNode; icon: ReactNode; tone?: "warning" }) {
  return (
    <div className={cn("flex gap-2 rounded-md border px-3 py-2.5 text-xs [&>svg]:mt-px [&>svg]:size-3.5 [&>svg]:shrink-0", tone === "warning" ? "border-warning/40 bg-warning/10 text-fg-2 [&>svg]:text-warning-text" : "bg-surface-2 text-fg-2 [&>svg]:text-fg-3")}>
      {icon}
      <span>{children}</span>
    </div>
  );
}

function UseCaseSelect({ id, value, onChange, useCases, agentIds, invalid, describedBy }: { id: string; value: string; onChange: (v: string) => void; useCases: UseCase[] | undefined; agentIds: string[]; invalid?: boolean; describedBy?: string }) {
  const inScope = (u: UseCase) => !agentIds.length || u.agentIds.some((a) => agentIds.includes(a));
  const visible = (useCases ?? []).filter((u) => u.status !== "hidden" && u.status !== "merged");
  const parents = visible.filter((u) => !u.parentId && inScope(u));
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={invalid || undefined} aria-describedby={describedBy} disabled={!useCases}>
      <option value="">{useCases ? "Pick a use case…" : "Loading use cases…"}</option>
      {parents.map((p) => (
        <optgroup key={p.id} label={p.name}>
          <option value={p.id}>{p.name} (all)</option>
          {visible
            .filter((c) => c.parentId === p.id)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
        </optgroup>
      ))}
    </Select>
  );
}

function StepEditor({
  step,
  index,
  count,
  errors,
  useCases,
  agentIds,
  ucName,
  onChange,
  onMove,
  onRemove,
}: {
  step: StepDraft;
  index: number;
  count: number;
  errors?: { name?: string; match?: string };
  useCases: UseCase[] | undefined;
  agentIds: string[];
  ucName: (id: string) => string | undefined;
  onChange: (patch: Partial<StepDraft>) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}) {
  const id = useId();
  const meta = STEP_KIND_META[step.kind];
  const nameValue = step.nameTouched ? step.name : effectiveName(step, ucName);
  const matchDescribed = errors?.match ? `${id}-match-error` : `${id}-match-hint`;
  return (
    <li className="grid grid-cols-[28px_minmax(0,1fr)_auto] gap-3 px-4 py-3">
      <span className="mt-6 grid size-6 place-items-center rounded-full border text-[11px] font-semibold tabular text-fg-2" aria-label={`Step ${index + 1}`}>
        {index + 1}
      </span>
      <div className="grid min-w-0 gap-3 md:grid-cols-[140px_minmax(0,1fr)]">
        <div>
          <Label htmlFor={`${id}-kind`}>Kind</Label>
          <Select id={`${id}-kind`} value={step.kind} onChange={(e) => onChange({ kind: e.target.value as FunnelStepKind, match: e.target.value === "outcome" ? "resolved" : "" })}>
            {STEP_KINDS.map((k) => (
              <option key={k} value={k}>
                {STEP_KIND_META[k].label}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <div>
            <Label htmlFor={`${id}-match`}>{meta.matchLabel}</Label>
            {step.kind === "outcome" ? (
              <Select id={`${id}-match`} value={step.match} onChange={(e) => onChange({ match: e.target.value })} aria-invalid={errors?.match ? true : undefined} aria-describedby={matchDescribed}>
                {SESSION_OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {OUTCOME_LABEL[o]}
                  </option>
                ))}
              </Select>
            ) : step.kind === "use_case" ? (
              <UseCaseSelect id={`${id}-match`} value={step.match} onChange={(v) => onChange({ match: v })} useCases={useCases} agentIds={agentIds} invalid={!!errors?.match} describedBy={matchDescribed} />
            ) : step.kind === "semantic" ? (
              <Textarea
                id={`${id}-match`}
                value={step.match}
                onChange={(e) => onChange({ match: e.target.value })}
                placeholder={meta.placeholder}
                maxLength={FUNNEL_LIMITS.match}
                className="min-h-14"
                aria-invalid={errors?.match ? true : undefined}
                aria-describedby={matchDescribed}
              />
            ) : (
              <Input
                id={`${id}-match`}
                value={step.match}
                onChange={(e) => onChange({ match: e.target.value })}
                placeholder={meta.placeholder}
                maxLength={FUNNEL_LIMITS.match}
                className="font-mono text-[12.5px]"
                aria-invalid={errors?.match ? true : undefined}
                aria-describedby={matchDescribed}
              />
            )}
            <FieldError
              id={`${id}-match`}
              error={errors?.match}
              hint={step.kind === "semantic" ? "Describe what happens in the conversation, e.g. “the agent handled a price objection”. Evaluated as a yes/no question on every session." : meta.help}
            />
          </div>
          <div className="max-w-md">
            <Label htmlFor={`${id}-name`}>Step name</Label>
            <Input
              id={`${id}-name`}
              value={nameValue}
              onChange={(e) => onChange({ name: e.target.value, nameTouched: true })}
              placeholder="Shown on the funnel chart"
              maxLength={FUNNEL_LIMITS.stepName}
              aria-invalid={errors?.name ? true : undefined}
              aria-describedby={errors?.name ? `${id}-name-error` : undefined}
            />
            <FieldError id={`${id}-name`} error={errors?.name} />
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-0.5">
        <Button type="button" variant="ghost" size="icon-sm" onClick={() => onMove(index - 1)} disabled={index === 0} aria-label={`Move step ${index + 1} up`}>
          <ArrowUp />
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" onClick={() => onMove(index + 1)} disabled={index === count - 1} aria-label={`Move step ${index + 1} down`}>
          <ArrowDown />
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" onClick={onRemove} disabled={count <= FUNNEL_LIMITS.steps.min} aria-label={`Remove step ${index + 1}`} title={count <= FUNNEL_LIMITS.steps.min ? "A funnel needs at least 2 steps" : undefined}>
          <Trash2 />
        </Button>
      </div>
    </li>
  );
}
